import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Disc3, Palette, Dices, Sparkles, ArrowRight, Coins, Lock, Megaphone, Plane, Spade, Layers, Flame, Users } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { formatCoins, avatarUrl } from "../lib/format";
import FestivalBanner from "../components/FestivalBanner";
import BholeSpin from "../components/BholeSpin";
import { getAppSettings } from "../lib/appSettings";

const BANNERS = ["/offer_addmoney.jpg", "/offer_invite.jpg", "/banner_spin.jpg", "/banner_jackpot.jpg", "/banner_bonus.jpg"];

const GAMES = [
  { to: "/app/aviator", icon: Plane, key: "aviator", tag: "1x – 100x", accent: "from-cyan-400/30 to-purple-600/10", glow: "glow-cyan", id: "aviator" },
  { to: "/app/spin", icon: Disc3, key: "spinWheel", tag: "1.5x – 100x", accent: "from-amber-400/30 to-amber-600/10", glow: "glow-gold", id: "spin" },
  { to: "/app/dragontiger", icon: Flame, key: "dragonTiger", tag: "1.95× / 8×", accent: "from-rose-400/30 to-orange-600/10", glow: "glow-magenta", id: "dragontiger" },
  { to: "/app/teenpatti", icon: Spade, key: "teenPatti", tag: "1.95×", accent: "from-rose-400/30 to-purple-600/10", glow: "glow-magenta", id: "teenpatti" },
  { to: "/app/andarbahar", icon: Layers, key: "andarBahar", tag: "1.95×", accent: "from-emerald-400/30 to-cyan-600/10", glow: "glow-cyan", id: "andarbahar" },
  { to: "/app/color", icon: Palette, key: "colorGame", tag: "up to 9×", accent: "from-fuchsia-400/30 to-purple-600/10", glow: "glow-magenta", id: "color" },
  { to: "/app/dice", icon: Dices, key: "diceGame", tag: "1 – 100", accent: "from-cyan-400/30 to-cyan-600/10", glow: "glow-cyan", id: "dice" },
  { to: "/app/scratch", icon: Sparkles, key: "scratchGame", tag: "x40 jackpot", accent: "from-emerald-400/30 to-emerald-600/10", glow: "glow-gold", id: "scratch" },
];

export default function Lobby() {
  const { user } = useAuth();
  const { t } = useLang();
  const [slide, setSlide] = useState(0);
  const [festivalImg, setFestivalImg] = useState(null);
  const [status, setStatus] = useState({});
  const [notices, setNotices] = useState([]);
  const [adminBanners, setAdminBanners] = useState([]);
  const [live, setLive] = useState(null);
  const [winners, setWinners] = useState([]);

  useEffect(() => {
    api.get("/games/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/announcements").then(({ data }) => {
      setNotices(data.filter((a) => a.type === "text"));
      setAdminBanners(data.filter((a) => a.type === "banner" && a.image_url).map((a) => avatarUrl(a.image_url)));
    }).catch(() => {});
    const loadLive = () => api.get("/live/players").then(({ data }) => setLive(data)).catch(() => {});
    const loadWinners = () => api.get("/live/winners").then(({ data }) => setWinners(data)).catch(() => {});
    getAppSettings().then((d) => { if (d.festival?.active && d.festival.banner_url) setFestivalImg(d.festival.banner_url); }).catch(() => {});
    loadLive(); loadWinners();
    const liveId = setInterval(loadLive, 20000);
    const winId = setInterval(loadWinners, 20000);
    const id = setInterval(() => setSlide((s) => s + 1), 4000);
    return () => { clearInterval(id); clearInterval(liveId); clearInterval(winId); };
  }, []);

  const banners = [...(festivalImg ? [festivalImg] : []), ...adminBanners, ...BANNERS];
  const current = banners.length ? slide % banners.length : 0;

  return (
    <div className="space-y-8">
      <FestivalBanner />
      {/* Banner carousel */}
      <div className="relative overflow-hidden rounded-2xl border border-amber-500/25 aspect-[16/7] sm:aspect-[21/7]" data-testid="banner-carousel">
        <AnimatePresence mode="wait">
          <motion.img
            key={current}
            src={banners[current]}
            alt=""
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-[#070B1F] via-transparent to-transparent" />
        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
          {banners.map((_, i) => (
            <button key={i} onClick={() => setSlide(i)} className={`h-1.5 rounded-full transition-all ${i === current ? "w-6 bg-amber-400" : "w-1.5 bg-white/40"}`} />
          ))}
        </div>
      </div>

      <BholeSpin />

      {/* Live players banner */}
      {live && (live.playing > 0 || live.online > 0) && (
        <div className="flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3" data-testid="live-players-banner">
          <span className="relative flex h-3 w-3 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
          </span>
          <Users size={18} className="text-emerald-300 shrink-0" />
          <p className="text-sm font-semibold text-emerald-100" data-testid="live-players-text">
            {live.playing > 0
              ? <><span className="font-mono-num font-bold text-emerald-300">{live.playing}</span> {t("playingNow")}</>
              : <><span className="font-mono-num font-bold text-emerald-300">{live.online}</span> {t("onlineNow")}</>}
          </p>
        </div>
      )}
      {live?.players?.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="live-player-ids">
          {live.players.map((p) => (
            <span key={p.user_id} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-black/30 px-3 py-1 text-xs" data-testid={`live-player-${p.user_id}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              <span className="font-mono-num text-emerald-200">{p.user_id}</span>
              {p.last_game && <span className="text-slate-500">· {p.last_game}</span>}
            </span>
          ))}
        </div>
      )}

      {/* Live winners ticker (FOMO) */}
      {winners.length > 0 && (
        <div className="relative overflow-hidden rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-amber-400/5 to-transparent py-2" data-testid="winners-ticker">
          <div className="flex w-max animate-marquee gap-8 whitespace-nowrap">
            {[...winners, ...winners].map((w, i) => (
              <span key={i} className="flex items-center gap-1.5 text-sm font-semibold text-amber-100">
                <span className="text-amber-400">🏆</span>
                <span className="gold-text font-bold">{w.name}</span>
                <span className="text-amber-200/80">ne</span>
                <span className="font-mono-num font-bold text-emerald-300">{formatCoins(w.amount)}</span>
                <span className="text-amber-200/80">jeete!</span>
              </span>
            ))}
          </div>
        </div>
      )}


      {notices.length > 0 && (
        <div className="space-y-2" data-testid="lobby-notices">
          {notices.map((n) => (
            <div key={n.id} className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3" data-testid={`notice-${n.id}`}>
              <Megaphone size={18} className="text-amber-400 mt-0.5 shrink-0" />
              <div>
                {n.title && <p className="font-display font-bold text-amber-200">{n.title}</p>}
                <p className="text-sm text-amber-100/90">{n.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Welcome + balance */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold gold-text">
          {user?.name ? `${t("welcome")}, ${user.name.split(" ")[0]}!` : t("brand")}
        </h1>
        <Link to="/app/payments" className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-black/50 px-4 py-2 hover:border-amber-400 transition-colors" data-testid="lobby-balance">
          <Coins size={18} className="text-amber-400" />
          <span className="font-mono-num font-bold text-amber-200 text-lg">{formatCoins(user?.coins)}</span>
          <span className="text-xs text-emerald-400">+ {t("addMoney")}</span>
        </Link>
      </div>

      {/* Games grid */}
      <div>
        <h2 className="font-display text-2xl font-bold mb-4">{t("lobby")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {GAMES.map((g, i) => {
            const disabled = status[g.id] === false;
            const inner = (
              <>
                <div className={`grid place-items-center h-16 w-16 rounded-2xl bg-gradient-to-br ${g.accent} border border-white/10`}>
                  <g.icon size={30} className="text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl font-bold">{t(g.key)}</h3>
                  <span className="text-xs font-mono-num text-amber-300/80">{g.tag}</span>
                </div>
                {disabled ? <Lock className="text-slate-500" /> : <ArrowRight className="text-amber-400 group-hover:translate-x-1 transition-transform" />}
              </>
            );
            return (
              <motion.div key={g.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
                {disabled ? (
                  <div className="mw-card flex items-center gap-4 p-5 opacity-50 cursor-not-allowed relative" data-testid={`game-card-${g.id}`}>
                    {inner}
                    <span className="absolute top-2 right-3 text-[10px] font-bold text-rose-400">बंद / OFF</span>
                  </div>
                ) : (
                  <Link to={g.to} data-testid={`game-card-${g.id}`} className={`mw-card mw-card-hover ${g.glow} flex items-center gap-4 p-5 group`}>
                    {inner}
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
